import type { PortfolioCategory } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { cn } from '@/shared/ui';

interface PortfolioCategoryTabsProps {
  readonly categories: PortfolioCategory[];
  readonly categoryId?: string;
  readonly onSelect: (categoryId?: string) => void;
}

/**
 * 포트폴리오 카테고리 탭 — 기획전 목적 탭·스토어 카테고리 탭과 같은 모양으로
 * 전폭 얇은 선 위에 "전체"와 카테고리를 한 줄로 두고, 선택한 탭은 굵은 2px 밑줄로 표시한다.
 * 좁은 화면에서는 가로로 밀어 본다.
 */
export const PortfolioCategoryTabs = ({
  categories,
  categoryId,
  onSelect,
}: PortfolioCategoryTabsProps): ReactElement => (
  <nav aria-label="포트폴리오 카테고리">
    {/* 탭이 넘치면 가로로 민다 — 얇은 선은 스크롤 폭 전체에 걸치도록 목록 자신에 긋는다. */}
    <div className="-mx-5 overflow-x-auto px-5 sm:mx-0 sm:px-0">
      <ul className="flex w-max min-w-full gap-7 border-b border-foreground/10 sm:gap-10">
        {[{ id: undefined, name: '전체' }, ...categories].map((category) => {
          const isSelected = category.id === categoryId;
          return (
            // 밑줄은 탭 줄의 얇은 선(1px) 위에 겹쳐 앉는다(-mb-px).
            <li key={category.id ?? 'all'} className="-mb-px shrink-0">
              <button
                type="button"
                aria-pressed={isSelected}
                onClick={() => onSelect(category.id)}
                className={cn(
                  'flex h-12 cursor-pointer items-center border-b-2 text-[15px] whitespace-nowrap transition-colors sm:text-base',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isSelected
                    ? 'border-foreground font-bold text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {category.name}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  </nav>
);
